import React, { useEffect, useState, useMemo } from 'react';
import Cookies from 'js-cookie';
import {
  StickyWrapper,
  Header as StyledHeader,
  ImageElement,
  ButtonWrapper,
  HeaderButton,
  Bonus,
  GiftButton,
  GiftProgress,
  GiftProgressWager,
  GiftTimer,
  BonusProgressMobile,
  DynamicBonusProgressMobile,
  ProcessInfo,
  FullscreenButton,
  FullScreenImage,
} from './styles';
import { Box } from '../box';
import { Button } from '../form/button';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useNotifications } from '@/shared/providers/notifications-provider/notifications-context';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import {
  BodyTextBoldQuinary,
  BodyTextBoldSecondary,
  BodyTextBoldTertiary,
} from '../typography';
import { Condition } from '../condition';
import { useI18n } from '@locales/client';

import { differenceInMilliseconds } from 'date-fns';
import GiftSpin from '@/shared/assets/gift-spin.svg';
import { useRouter, useSearchParams } from 'next/navigation';
import { formatCurrency } from '@/shared/libs/format-currency';
import { NotifyManagment } from '@/features/notify-managment';
import { useScrollTrigger } from '@/shared/hooks';
import { SelectLanguage } from '@/features/select-language';
import { useFullscreen } from '@/flat-pages/game/FullscreenContext';
import { LogoPart } from './components/logo-part';

const TIMEOUT_DURATION_MS = 20000;

const PingwinHeader = ({
  mobileLandscape,
  isLandscape,
  isPortrait,
  openWalletManagment,
  onOpen,
}) => {
  const {
    isFullscreen,
    handler: onFullscreenClick,
    needFullscreenButton,
  } = useFullscreen();

  const { user } = useUser();
  const { isNotifyLoading, notifications } = useNotifications();
  const isCookies = [
    Cookies.get('c_id_reg'),
    Cookies.get('c_id_auth'),
    Cookies.get('sessionId'),
  ];

  const [progressWager, setProgressWager] = useState(0);
  const [expiredDate, setExpireDate] = useState({
    mm: 0,
    hh: 0,
    percentage: null,
    isThirtyPercentRemaining: false,
  });

  const router = useRouter();
  const t = useI18n();

  const searchParams = useSearchParams();
  const isModalConfirmRecoveryCode = searchParams.get('recoveryCode');
  const isModal = searchParams.get('modal');

  const shouldShowModal = useMemo(
    () => !user.isAuth && !isModalConfirmRecoveryCode && !isModal,
    [user.isAuth, isModal, isModalConfirmRecoveryCode]
  );

  const headerScrolled = useScrollTrigger({
    triggerSelector: '[data-section]',
    triggerPosition: 70,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShowModal) {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);

        params.set(
          'modal',
          isCookies.some((el) => !!el) ? 'auth' : 'registration'
        );
        url.search = params.toString();
        router.push(url.toString());
      }
    }, TIMEOUT_DURATION_MS);

    return () => clearTimeout(timer);
  }, [shouldShowModal]);

  // Bonus expiration calculations
  useEffect(() => {
    if (
      notifications?.bonusInfo?.dateFrom &&
      notifications?.bonusInfo?.dateTo
    ) {
      const startDate = new Date(notifications.bonusInfo.dateFrom * 1000);
      const endDate = new Date(notifications.bonusInfo.dateTo * 1000);
      const totalTime = differenceInMilliseconds(endDate, startDate);
      const now = new Date();
      const remainingTime = differenceInMilliseconds(endDate, now);
      const hoursRemaining = Math.floor(remainingTime / (1000 * 60 * 60));
      const minutesRemaining = Math.floor(
        (remainingTime % (1000 * 60 * 60)) / (1000 * 60)
      );
      const percentageRemaining = (remainingTime / totalTime) * 100;
      const isThirtyPercentRemaining = percentageRemaining <= 30;

      setExpireDate({
        mm: minutesRemaining,
        hh: hoursRemaining,
        percentage: percentageRemaining,
        isThirtyPercentRemaining,
      });
    }
  }, [notifications?.bonusInfo?.dateFrom, notifications?.bonusInfo?.dateTo]);

  // Wager progress calculation
  useEffect(() => {
    if (notifications?.bonus?.wager && notifications?.bonus?.startWager) {
      const startWager = notifications.bonus.startWager;
      const currentWager = notifications.bonus.wager;
      const currentProgress = (currentWager / startWager) * 100;
      setProgressWager(currentProgress);
    }
  }, [notifications?.bonus?.wager, notifications?.bonus?.startWager]);

  if (isFullscreen) return null;

  return (
    <>
      <StickyWrapper $isSticky={true} $isScrolled={headerScrolled}>
        <StyledHeader>
          <LogoPart />

          <Condition match={user.isAuth}>
            <Box
              $display="flex"
              $width="100%"
              $justifyContent={
                mobileLandscape && notifications?.bonus?.isActive
                  ? 'space-between'
                  : 'flex-end'
              }
            >
              <Condition
                match={
                  notifications?.bonus?.isActive &&
                  notifications?.bonus?.giftspins === 0
                }
              >
                <Bonus>
                  <Box $position={'relative'}>
                    <GiftButton
                      onClick={() => router.push('/profile/bonuses')}
                    />
                    <Condition match={expiredDate.hh > 0}>
                      <GiftTimer
                        $isWarning={expiredDate.isThirtyPercentRemaining}
                      >
                        <Condition match={expiredDate.hh}>
                          <BodyTextBoldQuinary
                            color={
                              expiredDate.isThirtyPercentRemaining
                                ? '#EDEFF5'
                                : '#161639'
                            }
                          >
                            {expiredDate.hh}h
                          </BodyTextBoldQuinary>
                        </Condition>
                        <BodyTextBoldQuinary
                          color={
                            expiredDate.isThirtyPercentRemaining
                              ? '#EDEFF5'
                              : '#161639'
                          }
                        >
                          {expiredDate.mm}m
                        </BodyTextBoldQuinary>
                      </GiftTimer>
                    </Condition>
                    <GiftProgress
                      $isWarning={expiredDate.isThirtyPercentRemaining}
                      $percentage={expiredDate.percentage}
                    />
                  </Box>
                  <Box
                    $display={'flex'}
                    $flexDirection="column"
                    $justifyContent={'center'}
                  >
                    <Box $display="flex" $gap={16}>
                      <Box>
                        <BodyTextBoldSecondary>
                          {formatCurrency(notifications?.bonus?.bonusBalance, {
                            currency: user.currency,
                            currencySymbol: user.currencySymbol,
                          })}
                        </BodyTextBoldSecondary>
                      </Box>
                      <Condition match={!mobileLandscape}>
                        <Box $display="flex" $gap={4}>
                          <BodyTextBoldSecondary
                            color={
                              expiredDate.isThirtyPercentRemaining
                                ? '#DA4444'
                                : '#FFD60A'
                            }
                          >
                            {formatCurrency(notifications?.bonus?.wager, {
                              currency: user.currency,
                              currencySymbol: user.currencySymbol,
                            })}
                          </BodyTextBoldSecondary>
                          <BodyTextBoldSecondary>/</BodyTextBoldSecondary>
                          <BodyTextBoldSecondary color="#AFAFAF">
                            {formatCurrency(notifications?.bonus?.startWager, {
                              currency: user.currency,
                              currencySymbol: user.currencySymbol,
                            })}
                          </BodyTextBoldSecondary>
                        </Box>
                      </Condition>
                    </Box>
                    <Condition match={!mobileLandscape}>
                      <GiftProgressWager
                        $isWarning={expiredDate.isThirtyPercentRemaining}
                        $progress={progressWager}
                      />
                    </Condition>
                  </Box>
                </Bonus>
              </Condition>

              <Condition match={notifications?.bonus?.giftspins}>
                <Bonus>
                  <Box $position={'relative'}>
                    <GiftButton
                      onClick={() => router.push('/profile/bonuses')}
                    />
                    <GiftTimer
                      $isWarning={expiredDate.isThirtyPercentRemaining}
                    >
                      <Condition match={expiredDate.hh}>
                        <BodyTextBoldQuinary
                          color={
                            expiredDate.isThirtyPercentRemaining
                              ? '#EDEFF5'
                              : '#161639'
                          }
                        >
                          {expiredDate.hh}h
                        </BodyTextBoldQuinary>
                      </Condition>
                      <BodyTextBoldQuinary
                        color={
                          expiredDate.isThirtyPercentRemaining
                            ? '#EDEFF5'
                            : '#161639'
                        }
                      >
                        {expiredDate.mm}m
                      </BodyTextBoldQuinary>
                    </GiftTimer>
                    <GiftProgress
                      $isWarning={expiredDate.isThirtyPercentRemaining}
                      $percentage={expiredDate.percentage}
                    />
                  </Box>
                  <Box
                    $display={'flex'}
                    $flexDirection="column"
                    $justifyContent={'center'}
                  >
                    <Box $display="flex" $gap={16}>
                      <Box $display={'flex'} $gap={4}>
                        <Box>
                          <GiftSpin />
                        </Box>
                        <BodyTextBoldSecondary>
                          {notifications?.bonus?.giftspins}
                        </BodyTextBoldSecondary>
                      </Box>
                      <Condition match={!mobileLandscape}>
                        <Box $display="flex" $gap={4}>
                          <BodyTextBoldSecondary color={'#AFAFAF'}>
                            {formatCurrency(
                              notifications?.bonus?.bonusBalance,
                              {
                                currency: user.currency,
                                currencySymbol: user.currencySymbol,
                              }
                            )}
                          </BodyTextBoldSecondary>
                        </Box>
                      </Condition>
                    </Box>
                  </Box>
                </Bonus>
              </Condition>

              <Box $display="flex" $justifyContent="flex-end">
                <Condition
                  match={
                    !isNotifyLoading &&
                    notifications?.bonus?.giftspins === 0 &&
                    !notifications?.bonus?.isActive &&
                    notifications?.transaction?.status !== 'pending'
                  }
                >
                  {/* <Bonus $withoutBonus={true}>
                  <Box $position={'relative'}>
                    <GiftButton
                      onClick={() => router.push('/profile/bonuses')}
                      $withoutBonus={true}
                    ></GiftButton>
                  </Box>
                  <Condition match={notifications?.bonus?.count > 0}>
                    <Badge color="#DA4444" text={notifications?.bonus?.count} />
                  </Condition>
                </Bonus> */}
                </Condition>

                <Condition
                  match={
                    notifications?.transaction?.bonusID &&
                    notifications?.transaction?.status === 'pending'
                  }
                >
                  <Bonus $withoutBonus={true}>
                    <Box $position={'relative'}>
                      <GiftButton
                        onClick={() => router.push('/profile/bonuses')}
                        $loading={true}
                      />
                      <ProcessInfo>
                        <BodyTextBoldQuinary color="#161639">
                          в процессе
                        </BodyTextBoldQuinary>
                      </ProcessInfo>
                    </Box>
                  </Bonus>
                </Condition>

                <HeaderButton
                  $isEmptyBalance={notifications?.balance === 0}
                  forIcon={true}
                  isLoading={isNotifyLoading}
                  type="button"
                  onClick={openWalletManagment}
                  icon={
                    <ImageElement
                      style={{ display: 'flex' }}
                      width={20}
                      height={20}
                      src="/images/icons/wallet-balance.svg"
                    />
                  }
                  variant="orangeGradient"
                >
                  <Condition match={notifications?.bonus?.giftspins === 0}>
                    <Condition match={!notifications?.bonus?.bonusBalance}>
                      <BodyTextBoldSecondary>
                        <Condition match={notifications.balance === 0}>
                          {t('wallet.tabs.deposit')}
                        </Condition>
                        <Condition match={notifications.balance !== 0}>
                          {formatCurrency(notifications.balance, {
                            currency: user.currency,
                            currencySymbol: user.currencySymbol,
                          })}
                        </Condition>
                      </BodyTextBoldSecondary>
                    </Condition>
                  </Condition>
                </HeaderButton>
                {!(notifications?.bonus?.isActive && needFullscreenButton) && (
                  <NotifyManagment
                    mobileLandscape={mobileLandscape}
                    isLandscape={isLandscape}
                    isPortrait={isPortrait}
                  />
                )}
                {needFullscreenButton && (
                  <FullscreenButton
                    onClick={onFullscreenClick}
                    style={{ marginLeft: '8px' }}
                  >
                    <FullScreenImage />
                  </FullscreenButton>
                )}
              </Box>
            </Box>
          </Condition>

          <Condition match={!user.isAuth && !mobileLandscape}>
            <ButtonWrapper>
              <Button
                variant="orangeGradient"
                style={{ padding: '0px 32px' }}
                onClick={() => {
                  router.push('/?modal=registration');
                  // onOpen();
                }}
              >
                <BodyTextBoldSecondary>
                  {t('auth.sign_up')}
                </BodyTextBoldSecondary>
              </Button>
              <Button
                style={{ padding: '0px 32px' }}
                variant="gray"
                onClick={() => {
                  router.push('/?modal=auth');
                  // onOpen();
                }}
              >
                <BodyTextBoldSecondary style={{ padding: '0 8px' }}>
                  {t('auth.sign_in')}
                </BodyTextBoldSecondary>
              </Button>
              <SelectLanguage alignToLeft={false} isValueMode={true} />
            </ButtonWrapper>
          </Condition>
        </StyledHeader>
      </StickyWrapper>

      <Condition
        match={
          !isNotifyLoading && mobileLandscape && notifications?.bonus?.giftspins
        }
      >
        <BonusProgressMobile>
          <BodyTextBoldTertiary
            color={'#EDEFF5'}
            style={{
              textShadow: '0.5px 0.5px 1px #000',
            }}
          >
            {formatCurrency(notifications?.bonus?.bonusBalance, {
              currency: user.currency,
              currencySymbol: user.currencySymbol,
            })}
          </BodyTextBoldTertiary>
        </BonusProgressMobile>
      </Condition>

      <Condition
        match={
          !isNotifyLoading &&
          mobileLandscape &&
          notifications?.bonus?.isActive &&
          notifications?.bonus?.giftspins === 0 &&
          user.isAuth
        }
      >
        <DynamicBonusProgressMobile
          $isWarning={expiredDate.isThirtyPercentRemaining}
          $progress={progressWager}
        >
          <Box $display="flex" $gap={4} $justifyContent={'center'}>
            <BodyTextBoldSecondary
              style={{
                textShadow: '0.5px 0.5px 1px #000',
              }}
              color={'#EDEFF5'}
            >
              {formatCurrency(notifications?.bonus?.wager, {
                currency: user.currency,
                currencySymbol: user.currencySymbol,
              })}
            </BodyTextBoldSecondary>
            <BodyTextBoldSecondary
              style={{
                textShadow: '0.5px 0.5px 1px #000',
              }}
            >
              /
            </BodyTextBoldSecondary>
            <BodyTextBoldSecondary
              style={{
                textShadow: '0.5px 0.5px 1px #000',
              }}
              color="#EDEFF5"
            >
              {formatCurrency(notifications?.bonus?.startWager, {
                currency: user.currency,
                currencySymbol: user.currencySymbol,
              })}
            </BodyTextBoldSecondary>
          </Box>
        </DynamicBonusProgressMobile>
      </Condition>
    </>
  );
};

const PingwinHeaderWithBreakpointsSlice = withBreakpointsSlice(
  PingwinHeader,
  ({ mobileLandscape, isLandscape, isPortrait }) => ({
    mobileLandscape,
    isLandscape,
    isPortrait,
  })
);

export { PingwinHeaderWithBreakpointsSlice as PingwinHeader };
